import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, MapPin, Calendar, Leaf } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import FavoriteButton from "@/components/FavoriteButton";
import { localized } from "@/lib/projectI18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjektePage() {
  const { t, lang } = useLang();
  const FILTERS = [
    { id: "alle", label: t("projects.filter.all") },
    { id: "einfamilienhaus", label: t("projects.filter.einfamilienhaus") },
    { id: "reihenhaus", label: t("projects.filter.reihenhaus") },
    { id: "villa", label: t("projects.filter.villa") },
    { id: "stadthaus", label: t("projects.filter.stadthaus") },
    { id: "wohnbau", label: t("projects.filter.wohnbau") },
    { id: "sanierung", label: t("projects.filter.sanierung") },
    { id: "infrastruktur", label: t("projects.filter.infrastruktur") },
  ];
  const STATUS_FILTERS = ["alle", "Verfügbar", "In Entwicklung", "In Planung"];
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/projects`)
      .then((res) => setProjects(res.data))
      .catch((err) => console.warn("Projects load failed:", err?.message))
      .finally(() => setLoading(false));
    // API/axios are stable module-level references; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = projects.filter((p) => {
    if (filter !== "alle" && p.category !== filter) return false;
    if (statusFilter !== "alle" && p.status !== statusFilter) return false;
    return true;
  });

  return (
    <div data-testid="projekte-page" className="pt-32">
      <PageMeta pageKey="projects" />
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("projects.eyebrow")}</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {t("projects.title.l1")} <br />
          <span className="italic">{t("projects.title.l2")}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          {t("projects.lede")}
        </p>
      </section>

      {/* Filters */}
      <section className="border-y border-white/10 bg-[#0A2244]/40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <span className="text-[11px] tracking-[0.3em] uppercase text-white/50 mr-2">{t("projects.filter.category")}</span>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-medium border transition-colors ${
                  filter === f.id
                    ? "bg-white text-[#051630] border-white"
                    : "bg-transparent text-white/70 border-white/20 hover:border-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <span className="text-[11px] tracking-[0.3em] uppercase text-white/50 mr-2">{t("projects.filter.status")}</span>
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`status-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-medium border transition-colors ${
                  statusFilter === s
                    ? "bg-white text-[#051630] border-white"
                    : "bg-transparent text-white/70 border-white/20 hover:border-white"
                }`}
              >
                {s === "alle" ? t("projects.filter.all") : s}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        {loading ? (
          <div className="text-white/50 text-center py-24">{t("projects.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-white/50 text-center py-24">{t("projects.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 rh-stagger">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/projekte/${p.slug}`}
                data-testid={`project-card-${p.slug}`}
                className="group block border border-white/10 hover:border-white/40 transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={p.cover_image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 text-[10px] tracking-[0.25em] uppercase px-3 py-1.5">
                    {p.status}
                  </span>
                  <span
                    data-testid={`project-card-eco-badge-${p.slug}`}
                    className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 bg-emerald-400/15 backdrop-blur-md border border-emerald-300/40 text-emerald-200 text-[10px] tracking-[0.2em] uppercase px-3 py-1.5"
                  >
                    <Leaf size={11} strokeWidth={1.5} /> {t("projects.card.energy_class")}
                  </span>
                  <FavoriteButton slug={p.slug} className="absolute top-4 right-4" />
                </div>
                <div className="p-6 md:p-7">
                  <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-white/50 mb-3">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={12} strokeWidth={1.5} /> {p.location.split(",")[0]}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} strokeWidth={1.5} /> {p.year}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-[28px] font-medium tracking-tight leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed line-clamp-2">
                    {localized(p, "short_description", lang)}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-[11px] tracking-[0.25em] uppercase text-white/50">
                      {t("projects.card.details")}
                    </span>
                    <span className="w-10 h-10 border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-[#051630] group-hover:border-white transition-all">
                      <ArrowUpRight size={16} strokeWidth={1.5} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
